import { useEffect, useState } from "react";
import { Loader2, Plus, Video, Trash2, IndianRupee, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileUploader } from "@/components/FileUploader";
import { toast } from "sonner";

const emptyCourse = { title: "", tag: "", description: "", price_inr: "", duration: "", batch: "", image: "", instructor: "" };
const emptyLecture = { title: "", video_url: "", duration_sec: "" };

export const AdminCourses = ({ isSuper }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(null);
  const [courseOpen, setCourseOpen] = useState(false);
  const [courseForm, setCourseForm] = useState(emptyCourse);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState(emptyCourse);
  const [editSaving, setEditSaving] = useState(false);
  const [lectureFor, setLectureFor] = useState(null);
  const [lectureForm, setLectureForm] = useState(emptyLecture);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/courses");
      setCourses(data);
      setPrices(Object.fromEntries(data.map((c) => [c.course_id, c.price_inr])));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const savePrice = async (id) => {
    setSaving(id);
    try {
      await api.patch(`/admin/courses/${id}`, { price_inr: Number(prices[id]) });
      setCourses((prev) => prev.map((c) => (c.course_id === id ? { ...c, price_inr: Number(prices[id]) } : c)));
      toast.success("Price updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const addCourse = async () => {
    try {
      await api.post("/admin/courses", {
        ...courseForm,
        price_inr: Number(courseForm.price_inr),
      });
      toast.success("Course added");
      setCourseOpen(false);
      setCourseForm(emptyCourse);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (c) => {
    setEditFor(c.course_id);
    setEditForm({
      title: c.title || "",
      tag: c.tag || "",
      description: c.description || "",
      price_inr: c.price_inr ?? "",
      duration: c.duration || "",
      batch: c.batch || "",
      image: c.image || "",
      instructor: c.instructor || "",
    });
  };

  const saveEdit = async () => {
    if (!editFor) return;
    setEditSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        tag: editForm.tag.trim(),
        description: editForm.description,
        price_inr: Number(editForm.price_inr),
        duration: editForm.duration.trim(),
        batch: editForm.batch.trim(),
        image: editForm.image.trim(),
        instructor: editForm.instructor.trim(),
      };
      await api.patch(`/admin/courses/${editFor}`, payload);
      setCourses((prev) => prev.map((c) => (c.course_id === editFor ? { ...c, ...payload } : c)));
      setPrices((p) => ({ ...p, [editFor]: payload.price_inr }));
      toast.success("Course updated");
      setEditFor(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const addLecture = async () => {
    try {
      await api.post(`/admin/courses/${lectureFor}/modules`, {
        ...lectureForm, duration_sec: Number(lectureForm.duration_sec) || 0,
      });
      toast.success("Lecture added");
      setLectureFor(null);
      setLectureForm(emptyLecture);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm("Delete this course permanently?")) return;
    try {
      await api.delete(`/admin/courses/${courseId}`);
      setCourses((prev) => prev.filter((c) => c.course_id !== courseId));
      toast.success("Course deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteLecture = async (courseId, moduleId) => {
    if (!window.confirm("Delete this lecture?")) return;
    try {
      await api.delete(`/admin/courses/${courseId}/modules/${moduleId}`);
      toast.success("Lecture removed");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) {
    return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-red" /></div>;
  }

  return (
    <div data-testid="admin-courses-panel">
      <div className="mb-4 flex justify-between items-center">
        <div className="text-brand-navy font-semibold">{courses.length} courses</div>
        <Button data-testid="new-course-btn" onClick={() => setCourseOpen(true)} className="rounded-full bg-brand-red hover:bg-brand-red-hover">
          <Plus className="w-4 h-4 mr-1" /> Add course
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {courses.map((c) => (
          <div key={c.course_id} data-testid={`course-admin-row-${c.course_id}`} className="rounded-2xl bg-white border border-brand-navy/10 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {c.image && (
                  <img
                    src={c.image}
                    alt=""
                    className="w-14 h-14 rounded-xl object-cover ring-1 ring-brand-navy/10 shrink-0"
                    data-testid={`course-admin-thumb-${c.course_id}`}
                  />
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-bold text-brand-navy truncate">{c.title}</div>
                    {c.tag && <Badge variant="outline" className="text-[10px] shrink-0">{c.tag}</Badge>}
                  </div>
                  <div className="text-xs text-brand-navy/50 mt-1">
                    {c.duration || "—"} · {c.batch || "—"} · {(c.modules || []).length} lectures
                    {c.instructor ? ` · ${c.instructor}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={c.published ? "bg-emerald-600 hover:bg-emerald-600" : "bg-brand-navy hover:bg-brand-navy"}>
                  {c.published ? "Live" : "Draft"}
                </Badge>
                <button
                  data-testid={`edit-course-${c.course_id}`}
                  onClick={() => openEdit(c)}
                  className="text-brand-navy/60 hover:text-brand-navy"
                  aria-label="Edit course"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {isSuper && (
                  <button
                    data-testid={`delete-course-${c.course_id}`}
                    onClick={() => deleteCourse(c.course_id)}
                    className="text-brand-red/70 hover:text-brand-red"
                    aria-label="Delete course"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <div className="relative flex-1">
                <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-navy/40" />
                <Input
                  type="number"
                  data-testid={`course-price-input-${c.course_id}`}
                  value={prices[c.course_id] ?? ""}
                  onChange={(e) => setPrices({ ...prices, [c.course_id]: e.target.value })}
                  className="pl-9 rounded-full"
                />
              </div>
              <Button
                data-testid={`course-price-save-${c.course_id}`}
                onClick={() => savePrice(c.course_id)}
                disabled={saving === c.course_id || Number(prices[c.course_id]) === c.price_inr}
                className="rounded-full bg-brand-navy hover:bg-brand-red text-white"
              >
                {saving === c.course_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save price"}
              </Button>
            </div>

            {isSuper && (
              <div className="mt-4 pt-4 border-t border-brand-navy/10">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest font-bold text-brand-navy/50">Video lectures</div>
                  <Button
                    size="sm" variant="outline"
                    data-testid={`add-lecture-btn-${c.course_id}`}
                    onClick={() => { setLectureFor(c.course_id); setLectureForm(emptyLecture); }}
                    className="rounded-full text-xs"
                  >
                    <Video className="w-3.5 h-3.5 mr-1" /> Add lecture
                  </Button>
                </div>
                {(c.modules || []).length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {c.modules.map((m) => (
                      <li key={m.id} className="flex items-center justify-between text-sm text-brand-navy/80 bg-brand-muted-bg rounded-xl px-3 py-2">
                        <span className="truncate">{m.title}</span>
                        <button
                          data-testid={`delete-lecture-${m.id}`}
                          onClick={() => deleteLecture(c.course_id, m.id)}
                          className="text-brand-red/70 hover:text-brand-red shrink-0 ml-2"
                          aria-label="Delete lecture"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={courseOpen} onOpenChange={setCourseOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-navy">New course</DialogTitle>
            <DialogDescription className="text-brand-navy/60">Create a new course. Title and price are required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title</Label><Input data-testid="course-form-title" value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} /></div>
            <div><Label>Tag</Label><Input data-testid="course-form-tag" value={courseForm.tag} onChange={(e) => setCourseForm({ ...courseForm, tag: e.target.value })} placeholder="New · Bestseller" /></div>
            <div><Label>Instructor</Label><Input data-testid="course-form-instructor" value={courseForm.instructor} onChange={(e) => setCourseForm({ ...courseForm, instructor: e.target.value })} placeholder="e.g. Rohan Malhotra" /></div>
            <div><Label>Description</Label><Textarea data-testid="course-form-description" rows={3} value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Price (₹)</Label><Input data-testid="course-form-price" type="number" value={courseForm.price_inr} onChange={(e) => setCourseForm({ ...courseForm, price_inr: e.target.value })} /></div>
              <div><Label>Duration</Label><Input data-testid="course-form-duration" value={courseForm.duration} onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })} placeholder="6 weeks" /></div>
              <div><Label>Batch</Label><Input data-testid="course-form-batch" value={courseForm.batch} onChange={(e) => setCourseForm({ ...courseForm, batch: e.target.value })} placeholder="Mon · Wed" /></div>
            </div>
            <div>
              <Label>Thumbnail image</Label>
              <FileUploader
                accept="image/*"
                folder="courses"
                value={courseForm.image}
                onChange={(url) => setCourseForm({ ...courseForm, image: url })}
                maxSizeMB={10}
                testIdPrefix="course-form-image"
              />
            </div>
            <Button
              data-testid="course-form-save"
              onClick={addCourse}
              disabled={!courseForm.title.trim() || courseForm.price_inr === ""}
              className="w-full rounded-full bg-brand-red hover:bg-brand-red-hover"
            >
              Create course
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && !editSaving && setEditFor(null)}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="edit-course-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-navy">Edit course</DialogTitle>
            <DialogDescription className="text-brand-navy/60">Update course details. Changes save instantly to the live catalog.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title</Label><Input data-testid="edit-course-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
            <div><Label>Tag</Label><Input data-testid="edit-course-tag" value={editForm.tag} onChange={(e) => setEditForm({ ...editForm, tag: e.target.value })} placeholder="New · Bestseller" /></div>
            <div><Label>Instructor</Label><Input data-testid="edit-course-instructor" value={editForm.instructor} onChange={(e) => setEditForm({ ...editForm, instructor: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea data-testid="edit-course-description" rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Price (₹)</Label><Input data-testid="edit-course-price" type="number" value={editForm.price_inr} onChange={(e) => setEditForm({ ...editForm, price_inr: e.target.value })} /></div>
              <div><Label>Duration</Label><Input data-testid="edit-course-duration" value={editForm.duration} onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })} placeholder="6 weeks" /></div>
              <div><Label>Batch</Label><Input data-testid="edit-course-batch" value={editForm.batch} onChange={(e) => setEditForm({ ...editForm, batch: e.target.value })} placeholder="Mon · Wed" /></div>
            </div>
            <div>
              <Label>Thumbnail image</Label>
              <FileUploader
                accept="image/*"
                folder="courses"
                value={editForm.image}
                onChange={(url) => setEditForm({ ...editForm, image: url })}
                maxSizeMB={10}
                testIdPrefix="edit-course-image"
              />
            </div>
            <Button
              data-testid="edit-course-save"
              onClick={saveEdit}
              disabled={editSaving || !editForm.title.trim() || editForm.price_inr === "" || Number.isNaN(Number(editForm.price_inr))}
              className="w-full rounded-full bg-brand-red hover:bg-brand-red-hover"
            >
              {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lectureFor} onOpenChange={(o) => !o && setLectureFor(null)}>
        <DialogContent className="sm:max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-navy">Add video lecture</DialogTitle>
            <DialogDescription className="text-brand-navy/60">Upload an MP4 or paste a public video URL for this course.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Lecture title</Label><Input data-testid="lecture-form-title" value={lectureForm.title} onChange={(e) => setLectureForm({ ...lectureForm, title: e.target.value })} /></div>
            <div>
              <Label>Video (mp4)</Label>
              <FileUploader
                accept="video/*"
                folder="lectures"
                value={lectureForm.video_url}
                onChange={(url) => setLectureForm({ ...lectureForm, video_url: url })}
                maxSizeMB={2048}
                testIdPrefix="lecture-video-upload"
              />
            </div>
            <div><Label>Duration (seconds)</Label><Input data-testid="lecture-form-duration" type="number" value={lectureForm.duration_sec} onChange={(e) => setLectureForm({ ...lectureForm, duration_sec: e.target.value })} /></div>
            <Button
              data-testid="lecture-form-save"
              onClick={addLecture}
              disabled={!lectureForm.title.trim() || !lectureForm.video_url.trim()}
              className="w-full rounded-full bg-brand-red hover:bg-brand-red-hover"
            >
              Add lecture
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
